import React from 'react';

export interface MarginBandsProps {
    /** Margin a new variant is born with, in %. Drawn as the marker. */
    defaultMargin: number;
    /** Red below this margin, in %. */
    thresholdLow: number;
    /** Orange below this margin (green at or above), in %. */
    thresholdHigh: number;
    /** Text inside each zone. Dropped when the zone is too narrow for it. */
    zoneLabels: { low: string; mid: string; high: string };
    /** Shown under the bar — typically a worked price example built with
     *  `priceAtMargin`. */
    example?: React.ReactNode;
    /** Shown in red when the red threshold is above the orange one. */
    invertedMessage?: string;
    className?: string;
}

/** Price that yields `margin` % on `cost` — the same formula the variant
 *  editor uses (Precio = Coste / (1 − Margen/100)). */
export const priceAtMargin = (cost: number, margin: number): number =>
    margin >= 100 ? Infinity : cost / (1 - margin / 100);

const clampPct = (n: number) => Math.min(100, Math.max(0, Number.isFinite(n) ? n : 0));

/**
 * The margin traffic light as a 0–100 % bar: red zone, orange zone, green zone,
 * and a marker on the default margin. Shared by Tenant (Configuración → Costes
 * and the onboarding assistant) and Admin (Taller por defecto).
 *
 * Presentational only: the three editable fields stay with the caller, laid
 * out in the SAME order as the bar (red · orange · default) so each one sits
 * over the part of the bar it moves. No numbers are drawn on the bar — they
 * already are in those fields.
 */
const MarginBands: React.FC<MarginBandsProps> = ({
    defaultMargin,
    thresholdLow,
    thresholdHigh,
    zoneLabels,
    example,
    invertedMessage,
    className = '',
}) => {
    const lo = Math.min(clampPct(thresholdLow), clampPct(thresholdHigh));
    const hi = Math.max(clampPct(thresholdLow), clampPct(thresholdHigh));
    const mark = clampPct(defaultMargin);
    const inverted = thresholdLow > thresholdHigh;

    return (
        <div className={className}>
            {inverted && invertedMessage && (
                <p className="text-xs font-semibold text-red-600 dark:text-red-400 mb-2">{invertedMessage}</p>
            )}
            <div className="relative pt-3">
                <div className="flex h-7 rounded-lg overflow-hidden text-[11px] font-bold text-white">
                    <div className="flex items-center justify-center overflow-hidden whitespace-nowrap bg-red-500" style={{ width: `${lo}%` }}>
                        {lo >= 14 ? zoneLabels.low : null}
                    </div>
                    <div className="flex items-center justify-center overflow-hidden whitespace-nowrap bg-orange-500" style={{ width: `${hi - lo}%` }}>
                        {hi - lo >= 8 ? zoneLabels.mid : null}
                    </div>
                    <div className="flex flex-1 items-center justify-center overflow-hidden whitespace-nowrap bg-green-600">
                        {100 - hi >= 8 ? zoneLabels.high : null}
                    </div>
                </div>
                <span
                    className="absolute top-0 h-[calc(100%+6px)] w-[3px] -ml-[1.5px] rounded bg-slate-800 dark:bg-white pointer-events-none"
                    style={{ left: `${mark}%` }}
                    aria-hidden="true"
                />
                <span
                    className="absolute -top-1.5 h-3 w-3 -ml-1.5 rounded-full bg-slate-800 dark:bg-white pointer-events-none"
                    style={{ left: `${mark}%` }}
                    aria-hidden="true"
                />
            </div>
            <div className="flex justify-between text-[11px] text-text-sub dark:text-slate-500 mt-1.5">
                <span>0 %</span>
                <span>100 %</span>
            </div>
            {example && <p className="text-xs leading-relaxed text-text-sub dark:text-slate-400 mt-2">{example}</p>}
        </div>
    );
};

export default MarginBands;
